import type { AxiosInstance } from 'axios';
import type { CreateAxiosOptions } from './axiosTransform';

// ─── 类型定义 ────────────────────────────────────────────────────────────────

/** EventStream() 方法的请求配置，扩展自标准 fetch RequestInit */
export interface IEventStreamRequestConfig {
  /** 请求路径（相对路径或完整 URL） */
  url?: string;
  /** HTTP 方法，默认 POST */
  method?: string;
  /** 请求 body（非 GET 时序列化为 JSON） */
  data?: any;
  /** 取消控制器信号 */
  signal?: AbortSignal;
}

/** EventStream() 每次 yield 的事件结构，对应标准 EventStream 字段 */
export interface IEventStreamEvent<TData = string> {
  /** 事件类型，对应 EventStream `event:` 字段，默认 'message' */
  type: string;
  /** 事件 ID，对应 EventStream `id:` 字段 */
  id?: string;
  /** 已解析的数据，对应 EventStream `data:` 字段（自动 JSON.parse，失败则为原始字符串） */
  data: TData;
  /** 重试毫秒数，对应 EventStream `retry:` 字段 */
  retry?: number;
}

// ─── 内部工具函数 ─────────────────────────────────────────────────────────────

/**
 * 根据 axios 实例默认配置 + requestOptions.apiUrl 拼接完整请求 URL
 */
export function buildEventStreamUrl(
  rawUrl: string,
  axiosInstance: AxiosInstance,
  options: CreateAxiosOptions
): string {
  if (rawUrl.startsWith('http')) return rawUrl;
  const baseURL = ((axiosInstance.defaults.baseURL as string) ?? '').replace(/\/$/, '');
  const apiUrl = (options.requestOptions?.apiUrl ?? '').replace(/\/$/, '');
  return `${baseURL}${apiUrl}${rawUrl}`;
}

/**
 * 构造 EventStream 请求头：
 * - 先取 axios defaults 中的公共字符串头
 * - 再手动跑请求拦截器链（主要目的是注入 Authorization token）
 * - 最后强制补 Content-Type / Accept
 */
export async function buildEventStreamHeaders(
  fullUrl: string,
  config: IEventStreamRequestConfig,
  axiosInstance: AxiosInstance,
  options: CreateAxiosOptions
): Promise<Record<string, string>> {
  // 基础头
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };

  // 从 axios defaults 合并已有的公共字符串头
  const defaultHeaders = axiosInstance.defaults.headers as Record<string, any>;
  for (const key of Object.keys(defaultHeaders)) {
    const v = defaultHeaders[key];
    if (typeof v === 'string') headers[key] = v;
  }

  // 手动跑请求拦截器链取最终 headers（如 token 注入）
  try {
    const mockConfig: any = {
      url: fullUrl,
      method: config.method ?? 'POST',
      headers: { ...headers },
      data: config.data,
      requestOptions: options.requestOptions ?? {},
    };
    const handlers: Array<{ fulfilled?: Function }> =
      // @ts-ignore 访问 axios 内部拦截器队列
      axiosInstance.interceptors.request.handlers ?? [];
    let proceEventStreamd = mockConfig;
    for (const handler of handlers) {
      if (handler?.fulfilled) {
        proceEventStreamd = await Promise.resolve(handler.fulfilled(proceEventStreamd));
      }
    }
    Object.assign(headers, proceEventStreamd.headers as Record<string, string>);
  } catch {
    // 拦截器异常时降级，保持已有 headers
  }

  // 强制保留 EventStream 必要头（防止被拦截器覆盖）
  headers['Content-Type'] = 'application/json';
  headers['Accept'] = 'text/event-stream';

  return headers;
}

/**
 * 解析 EventStream 行格式（data: / event: / id: / retry:）
 * 维护字段缓冲，在收到空行时 yield 一个完整事件
 * 收到 `[DONE]` 或流结束时 return
 */
export async function* parseEventStreamStream<TData>(
  body: ReadableStream<Uint8Array>
): AsyncGenerator<IEventStreamEvent<TData>> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');

  // 当前事件字段缓冲
  let buffer = '';
  let eventType = 'message';
  let eventId = '';
  let eventData = '';
  let retryMs: number | undefined;

  try {
    while (true) {
      let done: boolean;
      let value: Uint8Array | undefined;
      try {
        ({ done, value } = await reader.read());
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        throw err;
      }

      if (value) buffer += decoder.decode(value, { stream: true });

      // 按行切割，尾部不完整的行留到下次
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const rawLine of lines) {
        const line = rawLine.replace(/\r$/, ''); // 兼容 \r\n

        if (line === '') {
          // 空行 → 派发当前事件
          if (eventData) {
            if (eventData.trim() === '[DONE]') return;
            yield makeEvent<TData>(eventType, eventId, eventData, retryMs);
          }
          // 重置字段缓冲
          eventType = 'message';
          eventId = '';
          eventData = '';
          retryMs = undefined;
          continue;
        }

        if (line.startsWith(':')) continue; // 注释行

        const colonIdx = line.indexOf(':');
        if (colonIdx === -1) continue; // 无值字段，跳过

        const field = line.slice(0, colonIdx);
        const fieldValue = line.slice(colonIdx + 1).replace(/^ /, ''); // 去掉首个空格

        switch (field) {
          case 'data':
            eventData = eventData ? `${eventData}\n${fieldValue}` : fieldValue;
            break;
          case 'event':
            eventType = fieldValue;
            break;
          case 'id':
            eventId = fieldValue;
            break;
          case 'retry': {
            const ms = parseInt(fieldValue, 10);
            if (!Number.isNaN(ms)) retryMs = ms;
            break;
          }
        }
      }

      if (done) {
        // 流关闭，派发尾部残留事件（服务端未以空行结束的情况）
        if (eventData && eventData.trim() !== '[DONE]') {
          yield makeEvent<TData>(eventType, eventId, eventData, retryMs);
        }
        return;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/** 将字段缓冲组装为 EventStreamEvent，data 字段尝试 JSON.parse */
function makeEvent<TData>(
  type: string,
  id: string,
  rawData: string,
  retry: number | undefined
): IEventStreamEvent<TData> {
  let data: TData;
  try {
    data = JSON.parse(rawData) as TData;
  } catch {
    data = rawData as unknown as TData;
  }
  return { type, id: id || undefined, data, retry };
}

// ─── 核心 EventStream generator ───────────────────────────────────────────────────────

/**
 * 基于 fetch + ReadableStream 的浏览器端 EventStream 流式请求
 *
 * - 复用 VAxios 实例的 baseURL / apiUrl 与请求拦截器（token 注入等）
 * - 返回 `AsyncGenerator<IEventStreamEvent<TData>>`，用 `for await...of` 逐事件消费
 * - 传入 `signal` 可随时取消（`AbortController.abort()`）
 * - 服务端发送 `[DONE]` 自动结束迭代
 * - 非 2xx 响应抛出带状态码的 Error
 *
 * @example
 * ```ts
 * const ctrl = new AbortController();
 * for await (const evt of EventStream({ url: '/la/qa/stream', data: { q: 'hi' }, signal: ctrl.signal }, instance, opts)) {
 *   console.log(evt.data);
 * }
 * ```
 */
export async function* axiosEventStream<TData = string>(
  config: IEventStreamRequestConfig,
  axiosInstance: AxiosInstance,
  options: CreateAxiosOptions
): AsyncGenerator<IEventStreamEvent<TData>> {
  const fullUrl = buildEventStreamUrl(config.url ?? '', axiosInstance, options);
  const headers = await buildEventStreamHeaders(fullUrl, config, axiosInstance, options);

  const method = (config.method ?? 'POST').toUpperCase();
  const hasBody = method !== 'GET' && method !== 'HEAD';

  let response: Response;
  try {
    response = await fetch(fullUrl, {
      method,
      headers,
      body: hasBody ? JSON.stringify(config.data ?? {}) : undefined,
      signal: config.signal,
      credentials: 'include',
    });
  } catch (err: any) {
    if (err?.name === 'AbortError') return;
    throw err;
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`[EventStream] HTTP ${response.status}: ${text}`);
  }
  if (!response.body) {
    throw new Error('[EventStream] Response body is null');
  }

  yield* parseEventStreamStream<TData>(response.body);
}
